import { TemplateTypeId } from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/templateTypeConfig'
import { PROFILE_AREA_ORDER } from '@app/src/pages/SustainabilityProfile/profileLogic'
import type { AssessmentQuestionnaireLink, QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { ScrapingType, SustainabilityArea } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopic } from '@app/src/types/topicalRisk'
import { EsgRequestTags } from '@app/src/wf-constants'

const AREA_BY_TAG: Partial<Record<string, SustainabilityArea>> = {
  [EsgRequestTags.Environmental]: SustainabilityArea.Environment,
  [EsgRequestTags.Social]: SustainabilityArea.Social,
  [EsgRequestTags.Governance]: SustainabilityArea.Governance,
}

const TAG_ONLY_CATEGORIES = [EsgRequestTags.Regulations, EsgRequestTags.Initiatives]

export type AreasByAssessmentTemplateId = Map<number, SustainabilityArea[]>

export const groupAreasByAssessmentTemplateId = (topics: SustainabilityTopic[]): AreasByAssessmentTemplateId =>
  topics.reduce((areasById, topic) => {
    const assessmentTemplateId = topic.assessmentTemplateId ?? topic.assessmentTemplateSection?.assessmentTemplateId
    if (assessmentTemplateId === undefined) return areasById

    const areas = areasById.get(assessmentTemplateId) ?? []
    areas.push(topic.sustainabilityArea)
    return areasById.set(assessmentTemplateId, areas)
  }, new Map<number, SustainabilityArea[]>())

export interface QuestionnaireRowSummary {
  kind: TemplateTypeId
  areas: SustainabilityArea[]
  tagCategories: EsgRequestTags[]
}

interface QuestionnaireRowContext {
  isBaseline: boolean
  solution?: string
  areasByAssessmentTemplateId: AreasByAssessmentTemplateId
}

const getAvailableAssessmentLinks = (
  template: QuestionnaireTemplate,
  solution?: string,
): AssessmentQuestionnaireLink[] => {
  if (!solution) return []

  return (template.assessmentQuestionnaireLink ?? []).filter(
    link =>
      !link.assessmentTemplate?.solutionAvailability?.length ||
      link.assessmentTemplate.solutionAvailability.some(
        availableSolution => availableSolution.toLowerCase() === solution.toLowerCase(),
      ),
  )
}

const getKind = (template: QuestionnaireTemplate, isAssessment: boolean, isBaseline: boolean): TemplateTypeId => {
  if (isAssessment) return TemplateTypeId.Assessment
  if (isBaseline || template.scrapingType === ScrapingType.AdverseScraping) return TemplateTypeId.AiScreening
  return TemplateTypeId.Questionnaire
}

export const getQuestionnaireRowSummary = (
  template: QuestionnaireTemplate,
  { isBaseline, solution, areasByAssessmentTemplateId }: QuestionnaireRowContext,
): QuestionnaireRowSummary => {
  const assessmentLinks = getAvailableAssessmentLinks(template, solution)
  const tags = template.tags ?? []
  const areas = new Set([
    ...assessmentLinks.flatMap(link => areasByAssessmentTemplateId.get(link.assessmentTemplateId) ?? []),
    ...tags.flatMap(tag => AREA_BY_TAG[tag] ?? []),
  ])

  return {
    kind: getKind(template, assessmentLinks.length > 0, isBaseline),
    areas: PROFILE_AREA_ORDER.filter(area => areas.has(area)),
    tagCategories: TAG_ONLY_CATEGORIES.filter(tag => tags.includes(tag)),
  }
}
